import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import Button from '../ui/Button';
import ReferralSection from '../ReferralSection';
import FreeTierBanner from '../FreeTierBanner';
import { useAuth } from '../../lib/AuthContext';
import { usePageMeta } from '../../lib/usePageMeta';
import {
    CreditCard, Key, History, Settings, LogOut,
    TrendingUp, Search, Download, Loader2, Copy, Check,
    AlertCircle
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface CreditLog {
    id: string;
    amount: number;
    type: string;
    reason: string;
    balance_after: number;
    created_at: string;
}

const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, logout, token, isLoading: authLoading } = useAuth();
    usePageMeta({ title: 'Dashboard', description: 'Manage your ProGeoData account, API keys, credits, and billing.', path: '/dashboard' });
    const [creditHistory, setCreditHistory] = useState<CreditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [copiedKey, setCopiedKey] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !token) {
            navigate({ to: '/auth' });
            return;
        }
        fetchCreditHistory(token);
    }, [navigate, user, token, authLoading]);

    const fetchCreditHistory = async (authToken: string) => {
        try {
            const res = await fetch(`${API_URL}/account/credits`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            if (res.ok) {
                const data = (await res.json()) as { history?: CreditLog[] };
                setCreditHistory(data.history || []);
            }
        } catch (err) {
            console.error('Failed to fetch credit history:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCopyApiKey = () => {
        if (user?.api_key) {
            navigator.clipboard.writeText(user.api_key);
            setCopiedKey(true);
            setTimeout(() => setCopiedKey(false), 2000);
        }
    };

    const handleLogout = () => {
        logout();
    };

    const handleUpgrade = () => {
        navigate({ to: '/' });
        setTimeout(() => {
            const pricingEl = document.getElementById('pricing');
            if (pricingEl) pricingEl.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
            </div>
        );
    }

    if (!user) return null;

    const tierColors: Record<string, string> = {
        free: 'bg-slate-600',
        starter: 'bg-sky-600',
        growth: 'bg-emerald-600',
        agency: 'bg-amber-600'
    };

    return (
        <div className="min-h-screen bg-slate-900 py-12 px-4">
            <meta name="robots" content="noindex" />
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white">Dashboard</h1>
                        <p className="text-slate-400">{user.email}</p>
                    </div>
                    <Button variant="outline" onClick={handleLogout}>
                        <LogOut className="w-4 h-4 mr-2" /> Sign Out
                    </Button>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        {error}
                    </div>
                )}

                {/* Free Tier Usage Banner */}
                {user.subscription_tier === 'free' && (
                    <FreeTierBanner dismissible={false} />
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Credits Card */}
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-slate-400 text-sm">Credits Remaining</span>
                            <CreditCard className="w-5 h-5 text-sky-500" />
                        </div>
                        <div className="text-4xl font-bold text-white mb-2">
                            {user.credits_remaining.toLocaleString()}
                        </div>
                        <p className="text-slate-500 text-sm">3 credits per search</p>
                        {user.credits_remaining < 100 && (
                            <Button variant="primary" size="sm" className="mt-4 w-full" onClick={handleUpgrade}>
                                Get More Credits
                            </Button>
                        )}
                    </div>

                    {/* Plan Card */}
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-slate-400 text-sm">Current Plan</span>
                            <TrendingUp className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${tierColors[user.subscription_tier] || 'bg-slate-600'}`}>
                                {user.subscription_tier.charAt(0).toUpperCase() + user.subscription_tier.slice(1)}
                            </span>
                        </div>
                        <p className="text-slate-500 text-sm">
                            {user.subscription_tier === 'free'
                                ? 'Upgrade for more credits'
                                : 'Active subscription'}
                        </p>
                        {user.subscription_tier === 'free' && (
                            <Button variant="outline" size="sm" className="mt-4 w-full" onClick={handleUpgrade}>
                                Upgrade Plan
                            </Button>
                        )}
                    </div>

                    {/* Quick Actions Card */}
                    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-slate-400 text-sm">Quick Actions</span>
                            <Settings className="w-5 h-5 text-slate-500" />
                        </div>
                        <div className="space-y-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-start"
                                onClick={() => navigate({ to: '/search' })}
                            >
                                <Search className="w-4 h-4 mr-2" /> Search Leads
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="w-full justify-start"
                                onClick={() => navigate({ to: '/bulk-export' })}
                            >
                                <Download className="w-4 h-4 mr-2" /> Bulk Export
                            </Button>
                        </div>
                    </div>
                </div>

                {/* API Key Section */}
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <Key className="w-5 h-5 text-amber-500" />
                        <h2 className="text-lg font-bold text-white">API Key</h2>
                    </div>
                    <p className="text-slate-400 text-sm mb-4">
                        Use this key to authenticate API requests. Keep it secret!
                    </p>
                    <div className="flex items-center gap-2">
                        <code className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sky-400 font-mono text-sm overflow-x-auto">
                            {user.api_key || '—'}
                        </code>
                        <Button variant="outline" onClick={handleCopyApiKey}>
                            {copiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                    </div>
                </div>

                {/* Referral Section */}
                <div className="mb-8">
                    <ReferralSection />
                </div>

                {/* Credit History Section */}
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <History className="w-5 h-5 text-sky-500" />
                        <h2 className="text-lg font-bold text-white">Credit History</h2>
                    </div>

                    {creditHistory.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No credit transactions yet</p>
                            <p className="text-sm">Purchase a plan to get started</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                                        <th className="pb-3">Date</th>
                                        <th className="pb-3">Type</th>
                                        <th className="pb-3">Description</th>
                                        <th className="pb-3 text-right">Amount</th>
                                        <th className="pb-3 text-right">Balance</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {creditHistory.map((log) => (
                                        <tr key={log.id} className="border-b border-slate-700/50">
                                            <td className="py-3 text-slate-300 text-sm">
                                                {new Date(log.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="py-3">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                    log.type === 'purchase' ? 'bg-emerald-500/20 text-emerald-400' :
                                                    log.type === 'usage' ? 'bg-sky-500/20 text-sky-400' :
                                                    'bg-slate-500/20 text-slate-400'
                                                }`}>
                                                    {log.type}
                                                </span>
                                            </td>
                                            <td className="py-3 text-slate-400 text-sm">{log.reason}</td>
                                            <td className={`py-3 text-right font-medium ${
                                                log.amount > 0 ? 'text-emerald-400' : 'text-red-400'
                                            }`}>
                                                {log.amount > 0 ? '+' : ''}{log.amount}
                                            </td>
                                            <td className="py-3 text-right text-slate-300">{log.balance_after}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
